import { ReactNodeViewRenderer } from '@tiptap/react';
import { InputRule, mergeAttributes, Node, nodePasteRule } from '@tiptap/core';

import { WidgetNodeView } from './MinswapWidgetNodeView';

// `[minswap ...]` when typed (fires on closing bracket). Should NOT be global for input rules.
const WIDGET_TYPED_REGEX = /^\[minswap(?:\s+([^\]]+))?\]$/;

// `[minswap ...]` when pasted
const WIDGET_PASTE_REGEX = /\[minswap(?:\s+([^\]]+))?\]/g;

/**
 * Convert a parameter string like `defaultAsset="MIN" foo="bar"` to an object
 * { defaultAsset: 'MIN', foo: 'bar' }.
 */
function parseWidgetParams(paramStr?: string): Record<string, string> {
  if (!paramStr) return {};
  const params: Record<string, string> = {};
  const regex = /(\w+)="([^"]*)"/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(paramStr)) !== null) {
    const [, key, value] = match;
    params[key] = value;
  }
  return params;
}

export const MinswapWidget = Node.create({
  name: 'MinswapWidget',
  addOptions() {
    return {
      addInputRules: true,
      HTMLAttributes: {},
      inline: false,
    };
  },
  inline() {
    return this.options.inline;
  },
  group() {
    return this.options.inline ? 'inline' : 'block';
  },
  draggable: true,
  addAttributes() {
    return {
      type: {
        default: 'MinswapWidget',
      },
      defaultAsset: {
        default: null,
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-MinswapWidget]',
      },
    ];
  },
  addInputRules() {
    return this.options.addInputRules
      ? [
          new InputRule({
            find: WIDGET_TYPED_REGEX,
            handler: ({ range, match, chain }) => {
              const paramStr = (match?.[1] as string | undefined) ?? undefined;
              const attrs = { type: 'MinswapWidget', ...parseWidgetParams(paramStr) };
              chain().insertContentAt(range, { type: this.name, attrs }).run();
            },
          }),
        ]
      : [];
  },

  addPasteRules() {
    return [
      nodePasteRule({
        type: this.type,
        find: WIDGET_PASTE_REGEX,
        getAttributes: (match: any) => {
          const paramStr = match[1] as string | undefined;
          return {
            type: 'MinswapWidget',
            ...parseWidgetParams(paramStr),
          };
        },
      }),
    ];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    console.log('renderHTML', HTMLAttributes);
    return [
      'div',
      mergeAttributes(
        this.options.HTMLAttributes,
        { 'data-widget': HTMLAttributes.type },
        HTMLAttributes.defaultAsset ? { 'data-default-asset': HTMLAttributes.defaultAsset } : {},
        HTMLAttributes,
      ),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(WidgetNodeView);
  },
});

export default MinswapWidget;
